/**
 * Google Identity Services (accounts.google.com/gsi/client) without a global script tag.
 *
 * The app must work without internet access (bank installations), so index.html does not load the
 * Google script. Login components (core login-v3 and project copies such as OKS Razpisi) call
 * google.accounts.id.* directly. This stub takes those calls, and the first initialize() with a
 * client_id loads the real script once and replays the calls on it. Without a client_id nothing is
 * loaded and the calls are no-ops.
 *
 * The real library sets each function onto the existing window.google.accounts.id object, so the
 * stub object stays and its functions are replaced in place.
 */
export const GOOGLE_IDENTITY_SRC = 'https://accounts.google.com/gsi/client';

const STUBBED = ['initialize', 'renderButton', 'prompt', 'disableAutoSelect', 'cancel'];

export function installGoogleIdentityStub(win: any = window, doc: Document = document): void {
  if (win.google?.accounts?.id) {
    return;
  }
  const queue: Array<[string, any[]]> = [];
  let scriptAdded = false;

  const load = () => {
    if (scriptAdded) {
      return;
    }
    scriptAdded = true;
    const script = doc.createElement('script');
    script.src = GOOGLE_IDENTITY_SRC;
    script.async = true;
    script.onload = () => {
      const id = win.google?.accounts?.id;
      delete win.google.__stub;
      for (const [name, args] of queue.splice(0)) {
        const fn = id?.[name];
        if (typeof fn === 'function' && !fn.__wgStub) {
          fn.apply(id, args);
        }
      }
    };
    doc.head.appendChild(script);
  };

  const id: any = {};
  for (const name of STUBBED) {
    const fn: any = (...args: any[]) => {
      if (name === 'initialize') {
        if (!args[0]?.client_id) {
          return;
        }
        queue.push([name, args]);
        load();
        return;
      }
      if (scriptAdded) {
        queue.push([name, args]);
      }
    };
    fn.__wgStub = true;
    id[name] = fn;
  }
  win.google = Object.assign(win.google || {}, { __stub: true, accounts: { id } });
}
